import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Plus, Edit2, Trash2, X, Flame } from 'lucide-react';

interface HebrewMonth {
  value: number;
  name: string;
}

interface Yahrzeit {
  id: string;
  deceasedName: string;
  deceasedHebrewName: string | null;
  relationship: string;
  hebrewDay: number;
  hebrewMonth: number;
  hebrewMonthName: string;
  hebrewYear: number | null;
  secularDate: string | null;
  observeOnShabbat: boolean;
  notes: string | null;
  createdAt: string;
}

const RELATIONSHIPS = [
  'Father',
  'Mother',
  'Grandfather',
  'Grandmother',
  'Spouse',
  'Son',
  'Daughter',
  'Brother',
  'Sister',
  'Uncle',
  'Aunt',
  'Friend',
  'Other',
];

export function YahrzeitsPage() {
  useAuth();
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    deceasedName: '',
    deceasedHebrewName: '',
    relationship: 'Father',
    hebrewDay: 1,
    hebrewMonth: 7, // Tishrei
    hebrewYear: '',
    secularDate: '',
    observeOnShabbat: true,
    notes: '',
  });

  const { data: hebrewMonths } = useQuery({
    queryKey: ['hebrew-months'],
    queryFn: async () => {
      const response = await api.get<HebrewMonth[]>('/yahrzeits/hebrew-months');
      if (!response.success) throw new Error(response.error?.message);
      return response.data || [];
    },
  });

  const { data: yahrzeits, isLoading } = useQuery({
    queryKey: ['my-yahrzeits'],
    queryFn: async () => {
      const response = await api.get<Yahrzeit[]>('/yahrzeits/my');
      if (!response.success) throw new Error(response.error?.message);
      return response.data || [];
    },
  });

  const createMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post('/yahrzeits', {
        deceasedName: formData.deceasedName,
        deceasedHebrewName: formData.deceasedHebrewName || undefined,
        relationship: formData.relationship,
        hebrewDay: formData.hebrewDay,
        hebrewMonth: formData.hebrewMonth,
        hebrewYear: formData.hebrewYear ? parseInt(formData.hebrewYear) : undefined,
        secularDate: formData.secularDate ? new Date(formData.secularDate).toISOString() : undefined,
        observeOnShabbat: formData.observeOnShabbat,
        notes: formData.notes || undefined,
      });
      if (!response.success) throw new Error(response.error?.message);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-yahrzeits'] });
      resetForm();
    },
    onError: (error: Error) => {
      alert(error.message);
    },
  });

  const updateMutation = useMutation({
    mutationFn: async () => {
      if (!editingId) return;
      const response = await api.patch(`/yahrzeits/${editingId}`, {
        deceasedName: formData.deceasedName,
        deceasedHebrewName: formData.deceasedHebrewName || undefined,
        relationship: formData.relationship,
        hebrewDay: formData.hebrewDay,
        hebrewMonth: formData.hebrewMonth,
        hebrewYear: formData.hebrewYear ? parseInt(formData.hebrewYear) : undefined,
        secularDate: formData.secularDate ? new Date(formData.secularDate).toISOString() : undefined,
        observeOnShabbat: formData.observeOnShabbat,
        notes: formData.notes || undefined,
      });
      if (!response.success) throw new Error(response.error?.message);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-yahrzeits'] });
      resetForm();
    },
    onError: (error: Error) => {
      alert(error.message);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await api.delete(`/yahrzeits/${id}`);
      if (!response.success) throw new Error(response.error?.message);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-yahrzeits'] });
    },
    onError: (error: Error) => {
      alert(error.message);
    },
  });

  const resetForm = () => {
    setShowForm(false);
    setEditingId(null);
    setFormData({
      deceasedName: '',
      deceasedHebrewName: '',
      relationship: 'Father',
      hebrewDay: 1,
      hebrewMonth: 7,
      hebrewYear: '',
      secularDate: '',
      observeOnShabbat: true,
      notes: '',
    });
  };

  const startEdit = (yahrzeit: Yahrzeit) => {
    setEditingId(yahrzeit.id);
    setFormData({
      deceasedName: yahrzeit.deceasedName,
      deceasedHebrewName: yahrzeit.deceasedHebrewName || '',
      relationship: yahrzeit.relationship,
      hebrewDay: yahrzeit.hebrewDay,
      hebrewMonth: yahrzeit.hebrewMonth,
      hebrewYear: yahrzeit.hebrewYear?.toString() || '',
      secularDate: yahrzeit.secularDate?.split('T')[0] ?? '',
      observeOnShabbat: yahrzeit.observeOnShabbat,
      notes: yahrzeit.notes || '',
    });
    setShowForm(true);
  };

  const handleSubmit = () => {
    if (!formData.deceasedName.trim()) {
      alert('Please enter the deceased name');
      return;
    }
    if (editingId) {
      updateMutation.mutate();
    } else {
      createMutation.mutate();
    }
  };

  const handleDelete = (id: string, name: string) => {
    if (confirm(`Are you sure you want to remove the yahrzeit for ${name}?`)) {
      deleteMutation.mutate(id);
    }
  };

  const formatHebrewDate = (day: number, monthName: string, year: number | null) => {
    return `${day} ${monthName}${year ? ` ${year}` : ''}`;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Yahrzeits</h1>
            <p className="mt-2 text-gray-600">
              Manage your family's yahrzeit observances
            </p>
          </div>
          {!showForm && (
            <button
              onClick={() => setShowForm(true)}
              className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700"
            >
              <Plus className="h-5 w-5" />
              Add Yahrzeit
            </button>
          )}
        </div>

        {/* Add/Edit Form */}
        {showForm && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900">
                {editingId ? 'Edit Yahrzeit' : 'Add New Yahrzeit'}
              </h2>
              <button
                onClick={resetForm}
                className="p-2 text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="grid gap-6 md:grid-cols-2">
              {/* Deceased Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Deceased Name (English) *
                </label>
                <input
                  type="text"
                  value={formData.deceasedName}
                  onChange={(e) => setFormData({ ...formData, deceasedName: e.target.value })}
                  placeholder="e.g., Abraham Smith"
                  className="w-full py-2 px-3 rounded-lg border border-gray-300 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              {/* Hebrew Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Hebrew Name (optional)
                </label>
                <input
                  type="text"
                  value={formData.deceasedHebrewName}
                  onChange={(e) => setFormData({ ...formData, deceasedHebrewName: e.target.value })}
                  placeholder="e.g., Avraham ben Yitzchak"
                  className="w-full py-2 px-3 rounded-lg border border-gray-300 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              {/* Relationship */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Relationship *
                </label>
                <select
                  value={formData.relationship}
                  onChange={(e) => setFormData({ ...formData, relationship: e.target.value })}
                  className="w-full py-2 px-3 rounded-lg border border-gray-300 focus:ring-primary-500 focus:border-primary-500"
                >
                  {RELATIONSHIPS.map((rel) => (
                    <option key={rel} value={rel}>{rel}</option>
                  ))}
                </select>
              </div>

              {/* Hebrew Date */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Hebrew Date of Passing *
                </label>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Day</label>
                    <select
                      value={formData.hebrewDay}
                      onChange={(e) => setFormData({ ...formData, hebrewDay: parseInt(e.target.value) })}
                      className="w-full py-2 px-3 rounded-lg border border-gray-300 focus:ring-primary-500 focus:border-primary-500"
                    >
                      {Array.from({ length: 30 }, (_, i) => i + 1).map((day) => (
                        <option key={day} value={day}>{day}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Month</label>
                    <select
                      value={formData.hebrewMonth}
                      onChange={(e) => setFormData({ ...formData, hebrewMonth: parseInt(e.target.value) })}
                      className="w-full py-2 px-3 rounded-lg border border-gray-300 focus:ring-primary-500 focus:border-primary-500"
                    >
                      {hebrewMonths?.map((month) => (
                        <option key={month.value} value={month.value}>{month.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Year (optional)</label>
                    <input
                      type="number"
                      value={formData.hebrewYear}
                      onChange={(e) => setFormData({ ...formData, hebrewYear: e.target.value })}
                      placeholder="5784"
                      className="w-full py-2 px-3 rounded-lg border border-gray-300 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                </div>
              </div>

              {/* Secular Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Secular Date (for reference)
                </label>
                <input
                  type="date"
                  value={formData.secularDate}
                  onChange={(e) => setFormData({ ...formData, secularDate: e.target.value })}
                  className="w-full py-2 px-3 rounded-lg border border-gray-300 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              {/* Observe on Shabbat */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="observeOnShabbat"
                  checked={formData.observeOnShabbat}
                  onChange={(e) => setFormData({ ...formData, observeOnShabbat: e.target.checked })}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <label htmlFor="observeOnShabbat" className="ml-2 text-sm text-gray-700">
                  Announce on preceding Shabbat
                </label>
              </div>

              {/* Notes */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notes
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={2}
                  placeholder="Any additional notes..."
                  className="w-full py-2 px-3 rounded-lg border border-gray-300 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6 pt-6 border-t">
              <button
                onClick={resetForm}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={createMutation.isPending || updateMutation.isPending}
                className="px-6 py-2 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 disabled:opacity-50"
              >
                {createMutation.isPending || updateMutation.isPending
                  ? 'Saving...'
                  : editingId
                  ? 'Update Yahrzeit'
                  : 'Add Yahrzeit'}
              </button>
            </div>
          </div>
        )}

        {/* Yahrzeits List */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          {isLoading ? (
            <div className="p-8 text-center">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent mx-auto" />
            </div>
          ) : !yahrzeits || yahrzeits.length === 0 ? (
            <div className="p-8 text-center">
              <Flame className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-1">No yahrzeits recorded</h3>
              <p className="text-gray-500 mb-4">
                Add yahrzeit dates to receive reminders and announcements
              </p>
              {!showForm && (
                <button
                  onClick={() => setShowForm(true)}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700"
                >
                  <Plus className="h-5 w-5" />
                  Add Your First Yahrzeit
                </button>
              )}
            </div>
          ) : (
            <div className="divide-y">
              {yahrzeits.map((yahrzeit) => (
                <div key={yahrzeit.id} className="p-4 hover:bg-gray-50">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      <div className="mt-1">
                        <Flame className="h-5 w-5 text-amber-500" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">
                          {yahrzeit.deceasedName}
                        </h3>
                        {yahrzeit.deceasedHebrewName && (
                          <p className="text-gray-600 text-sm">{yahrzeit.deceasedHebrewName}</p>
                        )}
                        <div className="mt-1 flex flex-wrap gap-2 text-sm">
                          <span className="text-gray-500">{yahrzeit.relationship}</span>
                          <span className="text-gray-300">|</span>
                          <span className="text-primary-600 font-medium">
                            {formatHebrewDate(yahrzeit.hebrewDay, yahrzeit.hebrewMonthName, yahrzeit.hebrewYear)}
                          </span>
                        </div>
                        {yahrzeit.notes && (
                          <p className="mt-2 text-sm text-gray-500">{yahrzeit.notes}</p>
                        )}
                        {yahrzeit.observeOnShabbat && (
                          <span className="mt-2 inline-block text-xs text-gray-400">
                            Announced on Shabbat
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => startEdit(yahrzeit)}
                        className="p-2 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg"
                        title="Edit"
                      >
                        <Edit2 className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(yahrzeit.id, yahrzeit.deceasedName)}
                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Info Section */}
        <div className="mt-8 bg-amber-50 rounded-xl p-6 border border-amber-200">
          <h3 className="text-lg font-semibold text-amber-900 mb-2">About Yahrzeits</h3>
          <p className="text-amber-800 text-sm leading-relaxed">
            A yahrzeit is the anniversary of a loved one's passing according to the Hebrew calendar.
            It is traditional to light a memorial candle, recite Kaddish, and give charity in memory
            of the deceased. By recording your yahrzeits, you'll receive reminders and the synagogue
            can announce the yahrzeit on the preceding Shabbat.
          </p>
        </div>
      </div>
    </div>
  );
}
